// src/hooks/useCategories.js
import { useState, useEffect, useCallback, useRef } from 'react'
import CategoryService from '../services/categoryService'

/**
 * Custom hook for managing categories with real-time updates
 * Handles fetching, caching, and auto-refresh of categories
 */
export const useCategories = ({
  autoRefresh = true,
  refreshInterval = 30000, // 30 seconds
  activeOnly = true,
  includeProductCount = false
} = {}) => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastFetch, setLastFetch] = useState(null)
  
  const refreshIntervalRef = useRef(null)
  const mountedRef = useRef(true)

  // Helper function to validate category object
  const validateCategory = (category) => {
    return (
      category &&
      typeof category === 'object' &&
      category._id &&
      category.name &&
      typeof category.name === 'string' &&
      category.name.trim().length > 0
    )
  }

  // Fetch categories from API
  const fetchCategories = useCallback(async (showLoading = true) => {
    try {
      console.log('🚀 fetchCategories called with showLoading:', showLoading)
      if (showLoading) setLoading(true)
      setError(null)
      
      console.log('📦 Fetching categories...', { 
        activeOnly, 
        includeProductCount,
        timestamp: new Date().toISOString()
      })
      
      // Get categories without requiring branch ID for POS
      const response = await CategoryService.getAllCategories()
      
      console.log('📦 CategoryService.getAllCategories() response:', response)
      
      if (!mountedRef.current) return // Component unmounted
      
      if (response && response.success) {
        let categoriesData = response.data || []
        
        console.log('🔍 DEBUG: Initial categoriesData structure:', {
          isArray: Array.isArray(categoriesData),
          hasCategories: categoriesData.categories ? true : false,
          hasData: categoriesData.data ? true : false,
          keysIfObject: !Array.isArray(categoriesData) ? Object.keys(categoriesData) : []
        });
        
        // Handle different response structures
        if (Array.isArray(categoriesData)) {
          console.log('🔍 DEBUG: Using direct array structure for categories');
          // Direct array - already in correct format
        } else if (categoriesData.categories && Array.isArray(categoriesData.categories)) {
          console.log('🔍 DEBUG: Using nested categories array structure, found:', categoriesData.categories.length, 'items');
          // Nested categories array
          categoriesData = categoriesData.categories;
        } else if (categoriesData.data && Array.isArray(categoriesData.data)) {
          console.log('🔍 DEBUG: Using double nested data array structure');
          // Double nested
          categoriesData = categoriesData.data;
        } else {
          console.warn('⚠️ Unexpected categories data structure:', categoriesData);
          categoriesData = [];
        }
        
        // Validate categories
        categoriesData = categoriesData.filter(validateCategory)
        
        // Filter active categories if requested
        if (activeOnly) {
          categoriesData = categoriesData.filter(category => 
            category.isActive !== false
          )
        }
        
        // Sort categories by name
        categoriesData.sort((a, b) => 
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        )
        
        console.log('✅ Categories loaded:', {
          total: categoriesData.length,
          activeOnly,
          categories: categoriesData.map(c => ({ id: c._id, name: c.name, active: c.isActive }))
        })
        
        setCategories(categoriesData)
        setLastFetch(new Date())
      } else {
        console.warn('⚠️ Unexpected categories response:', response)
        setError('Failed to load categories')
      }
    } catch (error) {
      console.error('❌ Error fetching categories:', error)
      
      if (!mountedRef.current) return
      
      // Set error for failed requests
      setError(error.message || 'Failed to load categories')
    } finally {
      if (mountedRef.current && showLoading) {
        setLoading(false)
      }
    }
  }, [activeOnly, includeProductCount])

  // Manual refresh function
  const refreshCategories = useCallback(() => {
    fetchCategories(true)
  }, [fetchCategories])

  // Setup auto-refresh
  useEffect(() => {
    if (!autoRefresh || !refreshInterval) return

    refreshIntervalRef.current = setInterval(() => {
      fetchCategories(false) // Don't show loading for auto-refresh
    }, refreshInterval)

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
    }
  }, [autoRefresh, refreshInterval, fetchCategories])

  // Initial fetch
  useEffect(() => {
    console.log('🚀 useCategories: Initial fetch effect triggered')
    console.log('🧪 TEZLAA DEBUG: Calling fetchCategories from useCategories hook')
    fetchCategories(true)
  }, [fetchCategories])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
    }
  }, [])

  // Helper functions for category operations
  const getCategoryById = useCallback((id) => {
    return categories.find(category => category._id === id)
  }, [categories])

  const getCategoryByName = useCallback((name) => {
    return categories.find(category => 
      category.name.toLowerCase() === name.toLowerCase()
    )
  }, [categories])

  const getActiveCategories = useCallback(() => {
    return categories.filter(category => category.isActive !== false)
  }, [categories])

  const getCategoriesCount = useCallback(() => {
    return {
      total: categories.length,
      active: categories.filter(c => c.isActive !== false).length,
      inactive: categories.filter(c => c.isActive === false).length
    }
  }, [categories])

  // Add category to local state (for real-time updates)
  const addCategory = useCallback((newCategory) => {
    if (!validateCategory(newCategory)) {
      console.warn('⚠️ Invalid category object:', newCategory)
      return false
    }

    setCategories(prev => {
      // Check if category already exists
      const exists = prev.some(cat => cat._id === newCategory._id)
      if (exists) {
        console.log('📦 Category already exists, updating:', newCategory.name)
        return prev.map(cat => 
          cat._id === newCategory._id ? newCategory : cat
        )
      }

      console.log('📦 Adding new category:', newCategory.name)
      return [newCategory, ...prev].sort((a, b) => 
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      )
    })
    return true
  }, [])

  // Update category in local state
  const updateCategory = useCallback((updatedCategory) => {
    if (!validateCategory(updatedCategory)) {
      console.warn('⚠️ Invalid category object:', updatedCategory)
      return false
    }

    setCategories(prev => 
      prev.map(cat => 
        cat._id === updatedCategory._id ? updatedCategory : cat
      ).sort((a, b) => 
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      )
    )
    return true
  }, [])

  // Remove category from local state
  const removeCategory = useCallback((categoryId) => {
    setCategories(prev => prev.filter(cat => cat._id !== categoryId))
  }, [])

  return {
    categories,
    loading,
    error,
    lastFetch,
    
    // Actions
    refreshCategories,
    addCategory,
    updateCategory,
    removeCategory,
    
    // Helpers
    getCategoryById,
    getCategoryByName,
    getActiveCategories,
    getCategoriesCount,
    
    // State info
    isEmpty: categories.length === 0,
    hasError: !!error,
    isStale: lastFetch && (Date.now() - lastFetch.getTime()) > refreshInterval * 2
  }
}